import { User as FirebaseUser } from "@firebase/auth-types";
import { Timestamp } from "@firebase/firestore-types";
import React from "react";
import { DatasetTypeEnum } from "./enums";
import { Plan as PlanEnum } from "@functions/plans/types";

export type FetchState<T> = { loading?: boolean; error?: Error | null; data?: T };
export interface SSRReactComponent extends React.FunctionComponent<any> {
    SSRElementQuery: string;
    SSRCache?: boolean;
}

export type Role = "creator" | "owner" | "labeler" | "reviewer";
type WorkspaceType = "user";
type HexCode = string;
export type RoboflowUser = FirebaseUser & { workspaces?: Record<string, Role> };
type UserId = RoboflowUser["uid"];
type LegacyPlan = "free" | "payg" | "growth" | "pro";
// TODO: Centralize in `@functions/plans` maybe like `@functions/plans/behavior.ts`
export type DowngradePlanTargets =
    | PlanEnum.UbpBasic
    | PlanEnum.UbpCore
    | PlanEnum.LegacySandbox
    | PlanEnum.Public;
type Plan = PlanEnum;
export type DatasetType = (typeof DatasetTypeEnum)[keyof typeof DatasetTypeEnum];
type ProjectType = DatasetType;
type RoboflowDomain = "roboflow.one" | "roboflow.com" | "roboflow-eu.one" | "roboflow.eu";
type RoboflowAPIKey = string;
export type DeploymentType = "hostedImage" | "hostedVideo" | "local" | "edge";

export enum ApiKeyDisabledReason {
    FOLDER_PAUSED = "folders_paused"
}

export interface ApiKey {
    id: RoboflowAPIKey;
    workspace: string;
    created_on?: number;
    default?: boolean;
    deviceId?: string;
    name?: string;
    projectGroupIds?: string[];
    scopes?: string[];
    // state management fields
    revokedAt?: Date; // Soft-delete field
    disabledAt?: Date; // New disabled field
    revoked?: boolean; // Legacy disabled field
    disabledReason?: ApiKeyDisabledReason;
}

export interface ApiKeyWithFolders extends ApiKey {
    projectGroups?: { id: ProjectGroup["id"]; name: ProjectGroup["name"] }[];
}

export interface WorkspaceMember {
    displayName: string;
    email: string;
    photoURL?: string;
}

export type WorkspaceCustomRole = {
    id: CustomRoleId;
    customRolePermissions: Record<string, boolean>;
    defaultBaseRole: Role;
    roleDisplayName: string;
    ssoAuthGroups?: string[];
};

export type CustomRoleId = string;

export type WorkspaceRoleId = Role | CustomRoleId;

export type WorkspaceCustomRoles = Record<CustomRoleId, WorkspaceCustomRole>;

export type WorkspaceCustomRolesFields = {
    customRolesEnabled?: boolean;
    superUserEnabled?: boolean;
    defaultWorkspaceRole?: Role | CustomRoleId | null;
    customRoles?: WorkspaceCustomRoles;
    userCustomRoles?: Record<UserId, Array<CustomRoleId>>;
    rolesWithAllFolderAccess?: WorkspaceRoleId[];
    usersWithAllFolderAccess?: UserId[];
};

export type WorkspaceSocialLinks = { github?: string; linkedin?: string; x?: string };

export type Workspace = WorkspaceCustomRolesFields & {
    id?: UserId;
    createdByEmailDomain?: string;
    type?: WorkspaceType;
    roles?: Record<UserId, Role>;
    members?: Record<UserId, WorkspaceMember>;
    workspaceId?: string;
    restricted?: boolean;
    name?: string;
    url?: string;
    projectCount?: number;
    restricted_reason?: "no_payment_method" | "past_due" | "churned";
    logo?: string;
    bgcolor?: string;
    verified?: boolean;
    description?: string;
    descriptionHTML?: string;
    descriptionMD?: string;
    socialLinks?: WorkspaceSocialLinks;
    allowedWorkflowBlocks?: string[];
};

export interface CycleUsageType {
    id: string;
    start_time: number; // timestamp in seconds
    end_time: number; // timestamp in seconds
    credits: number;
    featureCredits?: { [key: string]: number }; // e.g. image-source-storage: 4.704961111111114
    features?: { [key: string]: number }; // e.g. image-transform-storage: 7061.552777777785
    subscriptionId?: string;
}

export interface WorkspaceUsage {
    id: string;
    plan?: LegacyPlan | Plan | null;

    activeFlexStripeSubscriptionId?: string;
    activeFlexSubscription?: { currentPeriodEnd: Timestamp; currentPeriodStart: Timestamp };
    activePlanSubscription?: {
        status: "active" | "past_due";
        startDate: Timestamp;
        currentPeriodEnd: Timestamp;
        currentPeriodStart: Timestamp;
        cancelAt: Timestamp;
        isMonthly: boolean;
    };
    activeEnterpriseContract?: {
        currentPeriodEnd: Timestamp;
        currentPeriodStart: Timestamp;
        timezone?: string;
    };

    tensorrt?: { mau?: number; limit?: number };

    inference?: {
        quota?: number;
        currentMonth?:
            | "january"
            | "february"
            | "march"
            | "april"
            | "may"
            | "june"
            | "july"
            | "august"
            | "september"
            | "october"
            | "november"
            | "december";
        soft?: boolean;
        billing_period?: "monthly" | "annual";
        used: number;
        totalAnnualUsed: number;
    };

    limit?: {
        augmentationVariantsPerImage?: number;
        sources?: number;
        soft?: boolean;
        projects?: number;
        variantsPerImage?: number;
        transforms?: number;
        collaborators?: number;
        deviceLogsRetentionDays?: number;
    };

    deviceLogsRetention?: {
        appliedDays?: number | null;
        pendingApplyAt?: Timestamp | null;
        lastAppliedAt?: Timestamp | null;
    };

    train?: {
        used?: number;
        canTrainExtraLarge?: boolean;
        reload?: number;
        canTrainLegacyModels?: boolean; //TODO: remove when we remove legacy models
        day?: number;
        rollover?: boolean;
        quota?: number;
        pt?: boolean;
        modelLicense?: number;
        accurate?: boolean;
        canTrainSam3?: boolean;
        canTrainNAS?: boolean;
    };

    threshold?: number;
    workspace?: {
        team?: boolean;
        plan?: LegacyPlan | Plan | null;
        isSetup?: boolean;
        inheritedFrom?: Array<Workspace["id"]>;
    };

    restricted?: boolean;
    images?: { sources?: number; transforms?: number };
    tfjs?: { mau?: number; limit?: number };
    billing?: boolean;
    rates?: { sources?: number; transforms?: number };
    soft?: boolean;
    overage_billing_type?: string;
    oak?: { mau?: number; limit?: number };
    pro?: boolean;
    total?: number;
    features?: {
        creditSystem?: boolean;
        highComplexitySmartPoly?: boolean;
        vpcDeployment?: boolean;
        healthCheck?: boolean;
        exportVersion?: boolean;
        classAttributes?: boolean;
        advancedLabelAssist?: boolean;
        outsourcedAnnotations?: boolean;
        modelEvaluation?: boolean;
        emailUpload?: boolean;
        supportLevel?: "None";
        videoProcessing?: boolean;
        edgeInference?: "dev_only";
        workspaceInsights?: boolean;
        advancedTransforms?: boolean;
        cloudMlIntegrations?: boolean;
        deviceMonitoring?: boolean;
        deviceAio?: boolean;
        roboflowLiteMode?: boolean;
        tfjsInBrowser?: "dev_only";
        offlineMode?: boolean;
        rbac?: boolean;
        allowPrivateProjects?: boolean;
        annotationReview?: boolean;
        apiUpload?: boolean;
        embedInMobileApps?: boolean;
        hipaaCompliance?: boolean;
        refreshAnnotationsButton?: boolean;
        useProjectFoldersForWorkflows?: boolean;
        filterByTags?: boolean;
        folderBilling?: boolean;
        bucketMirroring?: boolean;
        billSelfHostedDeployments?: boolean;
        workflowBlockControls?: boolean;
        workflowsEditorV2Default?: boolean;
        canDisablePublicProjects?: boolean;
        billDeviceLogs?: boolean;
        downloadClassificationWeights?: boolean;
    };
    publicProjectsDisabled?: boolean;
    coreml?: { mau?: number; limit?: number };
    auto_label?: {
        monthly_quota: number;
        monthly_used: number;
        purchased_credits: number;
        purchased_used: number;
    };
    trial?: { isTrial: boolean; start_time: number; end_time: number };
    ubp?: {
        includedCredits: number;
        creditCap: number;
        pricePerCredit: number;
        flexPricePerCredit?: number;
        prepaidInCents?: number;
        prepaidCredits?: number;
        flexInvoiceThresholdInCents?: number;
    };
    promotions?: {
        freeTraining?: {
            isRedeemed: boolean;
            requiredImages: number;
            expiresAt: { seconds: number; nanoseconds: number };
        };
    };
    acceptedCancelOffer?: boolean;
    firstFailedInvoicePaymentDate?: Timestamp;
}

export interface SourceAnnotation {
    by?: string;
    converted: string; // stringified JSON
    original?: unknown;
}

export interface Workflow {
    id: string;
    config: string; // stringified JSON with specification object inside
    name: string;
    owner: string;
    url: string;
    lastVersionConfig?: string;
    rapidDataId?: string;
}

export interface Source {
    id: string;
    r: number;
    height: number;
    width: number;
    name: string;
    owner: string;
    extension: string;
    datasets: Dataset["id"][];
    projects: string[];
    projectGroupIds?: string[];
    tags?: string[];
    classes?: string[];
    annotation_jobs?: AnnotationJob["id"][];
    annotations?: Record<string, SourceAnnotation>;
    uploader?: string;
    accumulator?: { dataset?: string; icon?: string; add?: boolean };
}

export interface ProjectGroup {
    id: string;
    name: string;
    owner: string;
    imagesCount?: number;
    parent_id?: string;
    deletedAt?: Date; // Soft-delete field
}

// TODO: Add good typing with enums for augmentations and preprocessing - need to refactor generateOptions.js
export type VersionAugmentations = Record<string, any>;

export type VersionPreprocessing = Record<string, any>;

export interface VersionTrainInformation {
    auto: boolean;
    checkpoint: string | null;
    clusterKey: string;
    clusterName: string;
    endpoint: string;
    estimatedDuration: number;
    jobId: string;
    jobName: string;
    model: string;
    modelType: string;
    modelTypeDisplay: string;
    results: Record<string, any>;
    start: Timestamp;
    status: string;
    targetEpochs: number;
    trainService: string;
}

export interface Version {
    id: string;
    annotation?: string;
    augmentation?: VersionAugmentations;
    chunks?: Record<string, number>;
    classes?: Record<string, number>;
    exports?: Record<string, number>;
    generator?: string;
    images?: number;
    name?: string;
    notes?: string;
    preprocessing?: VersionPreprocessing;
    progress?: { generated?: number; failed?: number };
    splits?: {
        valid?: number;
        test?: number;
        export?: number;
        train?: number;
        testParsed?: number;
        testPercent?: number;
        trainParsed?: number;
        trainPercent?: number;
        validParsed?: number;
        validPercent?: number;
    };
    status?: string;
    totalImageResolution?: number;
    type?: DatasetType;
    train?: VersionTrainInformation;
    url?: string;
    classesBySplit?: {
        train?: Record<string, number>;
        valid?: Record<string, number>;
        test?: Record<string, number>;
    };
}

export interface Dataset {
    rapidDataId?: string;
    folderPath?: string[];
    group?: string; // project group id
    groupUpdatedAt?: Timestamp;
    preprocessing?: {
        "auto-orient"?: { enabled?: boolean };
        "resize"?: { format?: "Stretch to"; enabled?: boolean; width?: number; height?: number };
    };
    classes?: Record<string, number>;
    usedRoboflowAnnotate?: boolean;
    versions?: (string | number)[];
    type?: DatasetType;
    __data_version?: 1 | 2;
    _score?: number;
    url?: string;
    name?: string;
    totalImages?: number;
    images?: number;
    updated?: Timestamp;
    exports?: number;
    iconHasAnnotation?: boolean;
    labelingHours?: boolean;
    iconOwner?: Workspace["id"];
    splits?: {
        valid?: number;
        test?: number;
        export?: number;
        train?: number;
        testParsed?: number;
        testPercent?: number;
        trainParsed?: number;
        trainPercent?: number;
        validParsed?: number;
        validPercent?: number;
    };
    sample?: boolean;
    universe?: { stars?: number; tags?: string[]; workflowToken?: string; workflowUrl?: string };
    universeStats?: {
        totals?: { views?: number; downloads?: number };
        last30DaysTotals?: { views?: number; downloads?: number };
    };
    allClasses?: string[];
    limitedClasses?: { nofollow: boolean; name: string }[];
    multilabel?: boolean;
    classification?: boolean;
    migratedToTeamWorkspace?: boolean;
    modelCount?: number;
    slack?: number;
    colors?: Record<string, HexCode>;
    visibility?: Array<Workspace["id"]>;
    downloaded?: boolean;
    models?: number[];
    annotation?: string;
    version?: number;
    exported?: boolean;
    created?: Timestamp;
    owner?: Workspace["id"];
    ownerUser?: string | null;
    createdByUser?: string | null;
    icon?: string;
    id?: string;
    updatedMillis?: number;
    updatedRaw?: number;
    updatedAgo?: string;
    updatedTitle?: string;
    updatedMonthBibTeX?: "aug";
    workspace?: Workspace["id"];
    envDomain?: RoboflowDomain;
    cdnHost?: "source.roboflow.one" | "source.roboflow.com";
    publicName?: string;
    public?: boolean;
    typeDescription?: string;
    dir?: DatasetType;
    instanceSegmentation?: boolean;
    typeTitle?: string;
    trainedVersion?: boolean;
    chips?: Array<{
        id: "instance-segmentation" | "model";
        title: string;
        klass: "type" | "trained";
        url: string;
        depth: number;
    }>;
    numClasses?: number;
    classNames?: string;
    splitsWereEmpty?: boolean;
    imagesParsed?: string;
    imagesExact?: string;
    classCount?: number;
    unannotated?: 0;
    access_token?: RoboflowAPIKey;
    role?: Role;
    settings?: { modelEval?: { overlap?: number; iouThreshold?: number } };
}

export type Project = Dataset;

export interface ProjectWithId extends Project {
    id: string;
}

export interface AnnotationJob {
    uploaded?: Timestamp;
    numImages?: number;
    unannotated?: number;
    annotated?: number;
    rejected?: number;
    approved?: number;
    status?: "assigned" | "review" | "complete";
    createdBy?: RoboflowUser["uid"];
    labeler?: RoboflowUser["email"];
    reviewer?: RoboflowUser["email"];
    owner?: Workspace["id"];
    project?: Dataset["id"];
    sourceBatch?: string;
    name?: string;
    instructionsText?: string;
}

export interface Offer {
    promo: string;
    headingText?: string;
    dropdownText?: string;
    includedOffers?: string[];
    targetedDeal?: boolean;
}

export interface WorkspaceUBPSubscriptionData {
    isTiered: boolean;
    stripeCreditsFlexSubscription: any;
    stripeCreditsPrice: any;
    stripeHostedFlexSubscription: any;
    stripeSubscriptionMatchesUBP: boolean;
}

export interface PaidPlanSubscriptionData {
    brand: string | undefined;
    cancelAt: null | number;
    cancellationPending: boolean;
    customer: string | undefined;
    isActive: boolean;
    isAnnual: boolean;
    last4: string | undefined;
    paymentMethodDetails:
        | {
              brand: string | undefined;
              checks: any;
              country: string | undefined;
              display_brand: string | undefined;
              exp_month: number | undefined;
              // and more
          }
        | undefined;
    paymentMethodType: string | undefined;
    quantitiesPurchased: object | undefined;
    planUnitAmount: number | null | undefined;
    renewedAt: number | undefined;
    status: string | undefined;
    periodStart: number | undefined;
}

export interface Alert {
    group: string;
    type: string;
    level: "warning" | "error" | "info" | "success";
    used?: number;
    limit?: number;
    label: string;
    enabled?: boolean;
    message?: string;
    soft?: boolean;
    linkUrl?: string;
    linkText?: string;
}

export interface WorkspaceUsageAlert extends Alert {
    group:
        | "train"
        | "transforms"
        | "images"
        | "trial"
        | "reactivate"
        | "sale"
        | "inferences"
        | "unpaid-invoice"
        | "included-credits"
        | "custom-alert";

    type:
        | "quota"
        | "trial"
        | "sale"
        | "reactivate"
        | "free-train-gpu"
        | "unpaid-invoice"
        | "included-credits"
        | "custom-alert";

    /* Trial-specific: */
    daysRemaining?: number;
    isExpired?: boolean;
    isLastDay?: boolean;
}
export interface UseAlertsReturn<T extends Alert> {
    alerts: T[];
    muteAlert: (params: { alert: T; duration: number }) => void;
}

export enum FolderAlertType {
    USAGE = "folder-usage"
}

export enum FolderAlertGroup {
    USAGE_PAUSED = "paused"
}

export interface FolderAlert extends Alert {
    group: FolderAlertGroup.USAGE_PAUSED;
    type: FolderAlertType.USAGE;
}

export interface CronDocument {
    lastChecked: number;
}

export enum FolderUsageAction {
    PAUSE = "pause",
    RESUME = "resume",
    PAUSE_SELF_AND_DESCENDANTS = "pause-all-descendants",
    RESUME_SELF_AND_DESCENDANTS = "resume-all-descendants"
}

export enum FolderUsageErrorType {
    PAUSED = "FOLDER_USAGE_PAUSED",
    API_KEY_REQUIRED = "API_KEY_REQUIRED"
}

export const FolderUsageErrorMessages = {
    [FolderUsageErrorType.PAUSED]: "Folder Usage Paused",
    [FolderUsageErrorType.API_KEY_REQUIRED]: "API Key is required"
};

export const FolderUsageErrorHints = {
    [FolderUsageErrorType.PAUSED]:
        "Please contact your workspace administrator to re-enable api keys."
};

export enum FolderUsageFeature {
    LABEL_ASSIST = "Label Assist",
    VERSION_GENERATION = "Version Generation",
    PREVIEW_MODELS = "Preview Model",
    SMARTPOLYGON = "SmartPolygon",
    TRAIN_MODEL = "Train Model",
    BATCH_PROCESSING = "Batch Processing",
    BOX_PROMPTING = "Box Prompting",
    AUTO_LABEL = "Auto Label",
    UPLOAD = "Upload",
    CREATE_NEW_VERSION = "Version Create",
    SMART_SELECT = "Smart Select"
}

export interface PlanCycleInfo {
    cycleStart: Date;
    cycleEnd: Date;
    periodMs: number;
}

export interface ApiKeyData {
    id: string;
    prefix: string;
    name: string;
    projectGroupIds: string[];
}

export interface UsageBillingContext {
    includedCredits: number;
    prepaidCredits: number;
    includedAndPrepaidCredits: number;
    pricePerCredit: number;
    creditCap: number | "none" | null;
    flexEnabled: boolean;
    planCycle: PlanCycleInfo;
    flexCycle: PlanCycleInfo;
    cycleCreditsUsed: number;
}
